'use client'

import {useCallback, useState} from "react";
import Image from "next/image";
import {Skeleton} from "@/components/ui/skeleton";
import {cn} from "@/lib/utils";
import {ComingSoonPhoto} from "@/components/listings/coming-soon";

type Status = "loading" | "loaded" | "error";

/**
 * A photo that fills its (relatively positioned) parent. A pulsing skeleton shows
 * until the image has loaded, then the photo fades in; a photo that fails shows the
 * site's own "coming soon" picture instead of an endless skeleton. Key it by `src`
 * so the state resets when the photo changes. `fit="contain"` shows the whole photo
 * with no cropping (use it wherever a photo may carry a watermark); `className`
 * adds classes to the image, e.g. an `object-*` position.
 */
export default function ListingPhoto({src, alt, sizes, priority, fit = "cover", className, onLoaded}: {
    src: string;
    alt: string;
    sizes: string;
    priority?: boolean;
    fit?: "cover" | "contain";
    className?: string;
    onLoaded?: () => void;
}) {
    const [status, setStatus] = useState<Status>("loading");

    // A cached image can finish loading before React attaches onLoad, so check on mount.
    const attach = useCallback((img: HTMLImageElement | null) => {
        if (img?.complete) {
            if (img.naturalWidth > 0) {
                setStatus("loaded");
                onLoaded?.();
            } else if (img.currentSrc) {
                setStatus("error");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {status === "loading" ? (
                <Skeleton data-testid="photo-skeleton" className="absolute inset-0 rounded bg-muted-foreground/15 motion-reduce:animate-none" aria-hidden="true"/>
            ) : null}
            {status === "error" ? (
                <ComingSoonPhoto sizes={sizes}/>
            ) : (
                <Image
                    ref={attach}
                    src={src}
                    alt={alt}
                    fill
                    sizes={sizes}
                    unoptimized
                    priority={priority}
                    onLoad={() => {
                        setStatus("loaded");
                        onLoaded?.();
                    }}
                    onError={() => setStatus("error")}
                    className={cn(
                        "rounded transition-opacity duration-300 motion-reduce:transition-none",
                        fit === "cover" ? "object-cover" : "object-contain",
                        status === "loaded" ? "opacity-100" : "opacity-0",
                        className,
                    )}
                />
            )}
        </>
    );
}
